/**
 * Utility to generate SVG paths from GeoJSON coordinates for Nigerian states
 */

import type { StateGeoFeature } from '../types';

// Nigeria bounding box (approximate)
const NIGERIA_BOUNDS = {
  minLon: 2.6,
  maxLon: 14.7,
  minLat: 4.2,
  maxLat: 13.9,
};

/**
 * Convert GeoJSON coordinates to SVG path string
 */
export function coordinatesToSvgPath(
  coordinates: number[][][],
  width: number = 100,
  height: number = 100,
  bounds?: { minLon: number; maxLon: number; minLat: number; maxLat: number }
): string {
  const { minLon, maxLon, minLat, maxLat } = bounds || NIGERIA_BOUNDS;
  
  const scaleX = width / (maxLon - minLon);
  const scaleY = height / (maxLat - minLat);
  
  const pathParts: string[] = [];
  
  for (const ring of coordinates) {
    const points = ring.map(([lon, lat]) => {
      const x = (lon - minLon) * scaleX;
      // Flip Y axis since SVG Y increases downward
      const y = height - (lat - minLat) * scaleY;
      return [x, y] as [number, number];
    });
    
    if (points.length > 0) {
      pathParts.push(
        `M ${points[0][0].toFixed(2)},${points[0][1].toFixed(2)} ` +
        points.slice(1).map(([x, y]) => `L ${x.toFixed(2)},${y.toFixed(2)}`).join(' ') +
        ' Z'
      );
    }
  }
  
  return pathParts.join(' ');
}

/**
 * Get the bounding box for a state's coordinates
 */
export function getStateBounds(coordinates: number[][][]): {
  minLon: number;
  maxLon: number;
  minLat: number;
  maxLat: number;
} {
  let minLon = Infinity, maxLon = -Infinity;
  let minLat = Infinity, maxLat = -Infinity;
  
  for (const ring of coordinates) {
    for (const [lon, lat] of ring) {
      minLon = Math.min(minLon, lon);
      maxLon = Math.max(maxLon, lon);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    }
  }
  
  return { minLon, maxLon, minLat, maxLat };
}

/**
 * Generate SVG path for a single state (centered and scaled to fit)
 */
export function generateStateSvgPath(
  feature: StateGeoFeature,
  size: number = 100,
  padding: number = 10
): string {
  const coords = feature.geometry.type === 'Polygon'
    ? feature.geometry.coordinates as number[][][]
    : (feature.geometry.coordinates as number[][][][]).flat();
  
  const bounds = getStateBounds(coords);
  
  // Add padding to bounds
  const lonPadding = (bounds.maxLon - bounds.minLon) * (padding / size);
  const latPadding = (bounds.maxLat - bounds.minLat) * (padding / size);
  
  const paddedBounds = {
    minLon: bounds.minLon - lonPadding,
    maxLon: bounds.maxLon + lonPadding,
    minLat: bounds.minLat - latPadding,
    maxLat: bounds.maxLat + latPadding,
  };
  
  return coordinatesToSvgPath(coords, size, size, paddedBounds);
}

/**
 * Generate SVG paths for all states in Nigeria map
 */
export function generateNigeriaMapPaths(
  features: StateGeoFeature[],
  width: number = 400,
  height: number = 400
): Map<string, string> {
  const paths = new Map<string, string>();
  
  for (const feature of features) {
    const stateName = feature.properties.admin1Name;
    const coords = feature.geometry.type === 'Polygon'
      ? feature.geometry.coordinates as number[][][]
      : (feature.geometry.coordinates as number[][][][]).flat();
    
    const path = coordinatesToSvgPath(coords, width, height);
    paths.set(stateName, path);
  }
  
  return paths;
}

/**
 * Oil-producing states in Nigeria (Niger Delta)
 */
export const OIL_STATES = [
  'Rivers',
  'Akwa Ibom',
  'Delta',
  'Bayelsa',
  'Edo',
  'Ondo',
  'Abia',
  'Imo',
  'Cross River'
];

/**
 * Major oil states (top 3)
 */
export const MAJOR_OIL_STATES = ['Rivers', 'Akwa Ibom', 'Delta'];

/**
 * Northern states of Nigeria
 */
export const NORTHERN_STATES = [
  'Adamawa', 'Bauchi', 'Borno', 'Gombe', 'Taraba', 'Yobe', // North East
  'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Sokoto', 'Zamfara', // North West
  'Benue', 'Kogi', 'Kwara', 'Nasarawa', 'Niger', 'Plateau', 'FCT' // North Central
];

/**
 * Southern states of Nigeria
 */
export const SOUTHERN_STATES = [
  'Akwa Ibom', 'Bayelsa', 'Cross River', 'Delta', 'Edo', 'Rivers', // South South
  'Abia', 'Anambra', 'Ebonyi', 'Enugu', 'Imo', // South East
  'Ekiti', 'Lagos', 'Ogun', 'Ondo', 'Osun', 'Oyo' // South West
];

/**
 * Get the center point of a state for positioning
 */
export function getStateCenter(
  coordinates: number[][][],
  width: number = 100,
  height: number = 100
): { x: number; y: number } {
  const bounds = getStateBounds(coordinates);
  
  const scaleX = width / (NIGERIA_BOUNDS.maxLon - NIGERIA_BOUNDS.minLon);
  const scaleY = height / (NIGERIA_BOUNDS.maxLat - NIGERIA_BOUNDS.minLat);
  
  const centerLon = (bounds.minLon + bounds.maxLon) / 2;
  const centerLat = (bounds.minLat + bounds.maxLat) / 2;
  
  return {
    x: (centerLon - NIGERIA_BOUNDS.minLon) * scaleX,
    y: height - (centerLat - NIGERIA_BOUNDS.minLat) * scaleY,
  };
}

